import { blackScholes } from './blackScholes';

/**
 * Simulate a combination of option legs client-side via Black-Scholes.
 *
 * Each leg is priced at a simulated underlying (underlyingPrice * (1 + movePct/100))
 * with `days` of the holding period elapsed, and its P&L is the change from the
 * leg's current premium times contracts * 100 (long legs only; direction 'buy').
 *
 * @param {Array} legs - [{ label, option_type, direction, strike, dte, iv, premium, quantity }]
 *   iv is in percent (e.g. 72.2), matching the backend sim_legs payload.
 * @param {number} underlyingPrice - current underlying price (S0)
 * @param {number} days - days of the holding period elapsed (time decay)
 * @param {number} movePct - underlying move in percent (e.g. -10 for a 10% drop)
 * @param {number} riskFreeRate
 * @returns {{ totalPnl: number, simulatedUnderlying: number, perLeg: Array }}
 */
export function simulateLegs(legs, underlyingPrice, days, movePct, riskFreeRate = 0.05) {
  const simulatedUnderlying = underlyingPrice * (1 + movePct / 100);
  let totalPnl = 0;
  const perLeg = [];

  (legs || []).forEach((leg) => {
    const strike = Number(leg.strike);
    const dte = Number(leg.dte);
    const premium = Number(leg.premium);
    const qty = Number(leg.quantity) || 0;
    const ivDecimal = (Number(leg.iv) || 0) / 100;
    const optionType = leg.option_type;
    const sign = leg.direction === 'sell' ? -1 : 1;

    // Time remaining at the simulated exit point (in years).
    const T = Math.max((dte - days) / 365, 1 / 365);
    const simPremium = blackScholes(
      simulatedUnderlying, strike, T, riskFreeRate, ivDecimal, optionType
    );
    const pnl = sign * (simPremium - premium) * qty * 100;
    totalPnl += pnl;

    perLeg.push({
      label: leg.label,
      group: leg.group,
      simPremium,
      pnl,
    });
  });

  return { totalPnl, simulatedUnderlying, perLeg };
}

export default simulateLegs;
